export type AxiosCompatibleResponse<T = unknown> = {
  data: T;
  status: number;
  headers: Headers;
  url?: string;
  statusText?: string;
};

export type RequestConfig = {
  url?: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  data?: unknown;
  params?: Record<string, unknown>;
  withDeviceId?: boolean;
  withAuth?: boolean;
  withGeoHeaders?: boolean;
  withUserId?: boolean;
  cache?: 'no-store' | 'force-cache';
  revalidate?: false | number;
  keepalive?: boolean;
  httpsAgent?: unknown;
  timeout?: number;
};

/** The fetch init built in `request()`, as attached to a thrown error. */
export type BuiltRequestConfig = {
  headers: Headers;
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  cache?: 'no-store' | 'force-cache';
  next?: { revalidate?: false | number };
  keepalive?: boolean;
};

/** Thrown for any non-ok response. */
export class AxiosCompatibleError<T = unknown> extends Error {
  code?: number;
  config: BuiltRequestConfig;
  response: AxiosCompatibleResponse<T>;

  constructor({
    code,
    config,
    response,
  }: {
    code?: number;
    config: BuiltRequestConfig;
    response: AxiosCompatibleResponse<T>;
  }) {
    super(`HTTP ${code ?? response.status} ${config.method} ${response.url}`);

    Object.defineProperty(this, 'name', { value: 'AxiosCompatibleError' });
    this.code = code;
    this.config = config;
    this.response = response;
  }
}

export interface RawRowAndFieldError {
  row_number: number;
  field: string;
  message: string;
}

export type ParsedError =
  | {
      message?: string;
      error?: string;
      error_message?: string;
      file_level_error: string;
      row_level_errors: RawRowAndFieldError[];
    }
  | string
  | null;

export enum FetchState {
  NotStarted = 'notStarted',
  Fetching = 'fetching',
  Success = 'success',
  Error = 'error',
}

export type GenericErrorResponse = {
  status: number;
  message: string;
};
